'use client';

import { useEffect } from 'react';
import { reportError } from '@/lib/error-reporter';

/**
 * Hook that registers global error handlers for uncaught errors
 * and unhandled promise rejections, sending them to the API.
 *
 * Mount this once in a top-level layout or provider.
 * Also re-exports reportError for manual use in catch blocks.
 */
export function useErrorReporter() {
  useEffect(() => {
    // Handle uncaught errors
    const handleError = (event: ErrorEvent) => {
      // Opaque cross-origin errors (third-party scripts loaded without CORS, extensions): the
      // browser masks them as "Script error." with no Error object, filename or stack, so a report
      // would only wrap our own synthetic Error and can never be acted on.
      if (
        !(event.error instanceof Error) &&
        (/^script error\.?$/i.test((event.message || '').trim()) || !event.filename)
      ) {
        return;
      }
      reportError(
        event.error instanceof Error
          ? event.error
          : new Error(event.message || 'Unknown error'),
        {
          url: window.location.href,
          type: 'window.onerror',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }
      );
    };

    // Handle unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const error =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');

      reportError(error, {
        url: window.location.href,
        type: 'unhandledrejection',
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return { reportError };
}

export { reportError };
